/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    getTelemetryObject,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';

import { IInteractiveFeatureCollectionProps } from './interactive-feature-collection.props.autogenerated';

/**
 * Interactive Feature Collection view props.
 */
export interface IInteractiveFeatureCollectionViewProps extends IInteractiveFeatureCollectionProps<{}> {
    title: React.ReactNode;
    interactiveFeatureCollectionHeading: INodeProps;
    interactiveFeatureCollectionContainer: IModuleProps;
    interactiveFeatureCollectionDesktopContainer: INodeProps;
    interactiveFeatureCollectionDesktopItems: React.ReactNode[];
    interactiveFeatureCollectionMobileContainer: INodeProps;
    interactiveFeatureCollectionMobileItems: React.ReactNode[];
    singleSlideCarouselComponentProps: INodeProps;
    singleSlideCarouselItemContainer: INodeProps;
}

/**
 *
 * InteractiveFeatureCollection component.
 * @extends {React.PureComponent<IInteractiveFeatureCollectionProps<{}>>}
 */
export class InteractiveFeatureCollection extends React.PureComponent<IInteractiveFeatureCollectionProps<{}>> {
    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    private static _getFeatureItem(item: React.ReactNode, index: number, itemsCounter: number): React.ReactNode {
        const keyIndex = `interactive-feature-mosaic-item__${index}`;
        return (
            <React.Fragment key={keyIndex}>
                {React.cloneElement(item as React.ReactElement, { indexItem: index, itemCounter: itemsCounter, isMosaic: true })}
            </React.Fragment>
        );
    }

    public constructor(props: IInteractiveFeatureCollectionProps<IInteractiveFeatureCollectionViewProps>) {
        super(props);
    }

    public render(): JSX.Element | null {
        const { resources } = this.props;
        const { title, className } = this.props.config;
        const interactiveItems = this.props.slots && this.props.slots.interactiveFeatureItems;
        const itemsFeatureCounter = interactiveItems && interactiveItems.length;
        const singleSlideCarouselComponentProps: IComponentNodeProps<ISingleSlideCarouselProps> = {
            tag: SingleSlideCarousel,
            className: this.props.config.className ?? '',
            flipperPrevLabel: resources.flipperPrevious,
            flipperNextLabel: resources.flipperNext,
            parentId: this.props.id,
            telemetryContent: this._telemetryContent,
            vertical: false
        };
        const initItemsFeatureCounter = 0;
        const createHeading = title && (
            <Msdyn365.Text
                className='ms-interactive-feature__title'
                tag={title.tag ?? 'h1'}
                text={title.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );
        const viewProps = {
            ...this.props,
            title: createHeading,
            interactiveFeatureCollectionHeading: {
                className: 'ms-interactive-feature-collection__heading'
            },
            interactiveFeatureCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-interactive-feature-collection', className)
            },
            interactiveFeatureCollectionDesktopContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-desktop'
            },
            interactiveFeatureCollectionMobileContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-mobile'
            },
            singleSlideCarouselComponentProps,
            singleSlideCarouselItemContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-interactive-feature-collection-carousel',
                role: 'listitem'
            },
            interactiveFeatureCollectionMobileItems: this.props.slots.interactiveFeatureItems,
            interactiveFeatureCollectionDesktopItems:
                itemsFeatureCounter > initItemsFeatureCounter &&
                interactiveItems.map((item: React.ReactNode, index: number) =>
                    InteractiveFeatureCollection._getFeatureItem(item, index, itemsFeatureCounter)
                )
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * To handle text change.
     * @param event - To handle text change event.
     * @name - HandleTextChange
     * @public
     * @returns - The Text value.
     */
    public handleTextChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.title!.text = event.target.value);
}

export default InteractiveFeatureCollection;
